/**
 * Shared client checks of the organization forms (TO-389 organization-admin-ui
 * REQ-005…REQ-009). Each check returns an `organization` catalog key, or null.
 */

export type FieldErrors = Record<string, string>

export const COUNTRY_RE = /^[A-Z]{2}$/
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export const METADATA_MAX_BYTES = 16_384

/** Required text of 1…max characters after trimming. */
export const requiredText = (value: string, max: number): string | null => {
	const trimmed = value.trim()
	if (trimmed === '') return 'validation.required'
	return trimmed.length > max ? 'validation.tooLong' : null
}

/** Optional text of at most max characters after trimming. */
export const optionalText = (value: string, max: number): string | null =>
	value.trim().length > max ? 'validation.tooLong' : null

export const optionalCountry = (value: string): string | null =>
	value === '' || COUNTRY_RE.test(value) ? null : 'validation.country'

export const isUuid = (value: string): boolean => UUID_RE.test(value.trim())

/** validFrom ≤ validTo when both dates (YYYY-MM-DD) are set. */
export const validityError = (validFrom: string, validTo: string): string | null =>
	validFrom !== '' && validTo !== '' && validFrom > validTo ? 'validation.validity' : null

/** The JSON object in `text` (empty → `{}`), or null when it is not an object of ≤16384 bytes. */
export const parseMetadata = (text: string): Record<string, unknown> | null => {
	if (text.trim() === '') return {}
	let value: unknown
	try {
		value = JSON.parse(text)
	} catch {
		return null
	}
	if (value === null || typeof value !== 'object' || Array.isArray(value)) return null
	return new TextEncoder().encode(JSON.stringify(value)).length > METADATA_MAX_BYTES
		? null
		: (value as Record<string, unknown>)
}

/** A date input value (YYYY-MM-DD) as the API's UTC midnight timestamp, or null when empty. */
export const dateToIso = (date: string): string | null => (date === '' ? null : `${date}T00:00:00.000Z`)

/** An API timestamp as a date input value. */
export const isoToDate = (iso: string | null): string => (iso ? iso.slice(0, 10) : '')

/** Trimmed text, or null when empty. */
export const textOrNull = (value: string): string | null => {
	const trimmed = value.trim()
	return trimmed === '' ? null : trimmed
}

/** Drop the null checks; keep `field → key`. */
export const collect = (checks: Record<string, string | null>): FieldErrors =>
	Object.fromEntries(Object.entries(checks).filter((entry): entry is [string, string] => entry[1] !== null))

/** The entries of `next` that differ from `current` (by JSON value). */
export const diff = (current: Record<string, unknown>, next: Record<string, unknown>): Record<string, unknown> =>
	Object.fromEntries(
		Object.entries(next).filter(([key, value]) => JSON.stringify(current[key]) !== JSON.stringify(value)),
	)
